import { type AirStep, coachSide, formatEventMinute } from "@livcg/schema";

type Side = "home" | "away";
type GoalType = "goal" | "penalty_goal" | "own_goal";
export type StepPlayer = { id: string; firstName: string; lastName: string; number: number | null };
export type Swap = { outNumber: number; outName: string; inNumber: number; inName: string };

const GOAL_BANNER_MS = 4000;
const SCORER_BANNER_MS = 4000;
const CARD_BANNER_MS = 5000;
const SWAP_BANNER_MS = 9500;
const SWAP_LEAD_IN_MS = 600;
const GOAL_FLASH_MS = 5000;
const CANCEL_BANNER_MS = 5000;

const other = (side: Side): Side => (side === "home" ? "away" : "home");
const surnameOf = (p: StepPlayer) => (p.lastName || p.firstName).trim();

export function chainKey(type: string, side: Side, playerId: string): string {
  return `${type}:${side}:${playerId}`;
}

export function goalSteps(
  type: GoalType,
  side: Side,
  player: StepPlayer,
  minute: number,
  second: number,
  added?: number | null,
): AirStep[] {
  const bannerSide = type === "own_goal" ? other(side) : side;
  const tag = type === "own_goal" ? " (AG)" : "";
  const scorerName = `${surnameOf(player).toUpperCase()} ${formatEventMinute(minute, second, added)}ʼ${tag}`;
  return [
    { mode: `little_event_goal_${bannerSide}`, data: {}, holdMs: GOAL_BANNER_MS },
    { mode: `little_event_scorer_${bannerSide}`, data: { scorerName }, holdMs: SCORER_BANNER_MS },
  ];
}

export function cardSteps(type: string, side: Side, player: StepPlayer): AirStep[] {
  const kind =
    type === "yellow_card" ? "yellow" : type === "second_yellow" ? "secondyellow" : "red";
  return [
    {
      mode: `little_card_${kind}_${side}`,
      data: {
        playerNumber: player.number ?? (coachSide(player.id) ? "" : 0),
        playerName: surnameOf(player),
      },
      holdMs: CARD_BANNER_MS,
    },
  ];
}

export function subsSteps(side: Side, swaps: Swap[], currentMode: string | null): AirStep[] {
  const mode = `little_card_swap_${side}`;
  const banner: AirStep = { mode, data: { swaps }, holdMs: SWAP_BANNER_MS };
  // The same banner already on air would not re-enter — a short scoreboard first.
  return currentMode === mode
    ? [{ mode: "scoreboard", data: {}, holdMs: SWAP_LEAD_IN_MS }, banner]
    : [banner];
}

export function goalFlashSteps(side: Side): AirStep[] {
  return [{ mode: `little_event_goal_${side}`, data: {}, holdMs: GOAL_FLASH_MS }];
}

export function cancelSteps(mode: string, data: unknown): AirStep[] {
  return [{ mode, data, holdMs: CANCEL_BANNER_MS }];
}

export const PENDING_CHAIN_MS = 1500;

/** The on-air chain key: the replicant's once it shows a sequence, else the one this
 *  browser just fired for a short window (the round trip before the replicant catches up). */
export function effectiveChainKey(
  replicantKey: string | null,
  pending: { key: string; at: number } | null,
  now: number,
): string | null {
  if (replicantKey) return replicantKey;
  return pending && now - pending.at < PENDING_CHAIN_MS ? pending.key : null;
}
